/**
 * Throttle a function
 */
export function throttle(func: unknown, wait: number, options?: unknown): (...args: Array<unknown>) => unknown {
  options = options || {};
  let result: unknown;
  let timeout: unknown = null;
  let previous = 0;

  function later(context: unknown, args: Array<unknown>): void {
    previous = (options as Record<string, unknown>).leading === false ? 0 : +new Date();
    timeout = null;
    result = (func as (...a: Array<unknown>) => unknown).apply(context, args);
  }

  return function (this: unknown, ...args: Array<unknown>): unknown {
    const now = +new Date();

    if (!previous && (options as Record<string, unknown>).leading === false) {
      previous = now;
    }

    const remaining = wait - (now - previous);

    if (remaining <= 0) {
      clearTimeout(timeout as ReturnType<typeof setTimeout>);
      timeout = null;
      previous = now;
      result = (func as (...a: Array<unknown>) => unknown).apply(this, args);
    } else if (!timeout && (options as Record<string, unknown>).trailing !== false) {
      timeout = setTimeout(() => later(this, args), remaining);
    }

    return result;
  };
}

/**
 * Throttle decorator
 *
 *  class MyClass {
 *    throttleable(10)
 *    myFn() { ... }
 *  }
 */
export function throttleable(duration: number, options?: unknown): (_target: unknown, key: PropertyKey, descriptor: PropertyDescriptor) => PropertyDescriptor {
  return function innerDecorator(_target: unknown, key: PropertyKey, descriptor: PropertyDescriptor): PropertyDescriptor {
    return {
      configurable: true,
      enumerable: descriptor.enumerable,
      get: function getter(): unknown {
        Object.defineProperty(this, key, {
          configurable: true,
          enumerable: descriptor.enumerable,
          value: throttle(descriptor.value, duration, options)
        });

        return (this as Record<PropertyKey, unknown>)[key];
      }
    };
  };
}
